import { useEffect, useMemo, useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";

import { useAuthStore } from "src/components/main/authStore";
import LocatorForm from "src/components/outside/locator/locatorForm";
import LocatorEmergencyForm from "src/components/outside/locator/locatorEmergencyForm";
import {
    buildWeekNumArray,
    defaultEmptyLocatorDataString,
    getDateEndFromWeekNum,
    getDatefromWeekNum,
    locatorStartDate,
    MyDate,
    myAxios,
    statusColorCode,
    weeknum,
    weekDays,
} from "src/components/common/functions";

type Division = {
    division: string;
    supervisorName?: string;
    supervisorEmail?: string;
    role?: string;
};

type LocatorDay = Record<string, any> & {
    status?: string;
    workStatus?: string;
    id?: string;
    date?: string;
};

type LocatorWeekForm = {
    form: LocatorDay[];
    emergency?: LocatorDay[];
    status?: string;
    submitTime?: string;
};

type LoaderData = {
    week: string;
    form: LocatorWeekForm;
    divisions: Division[];
};

export default function Locator() {
    const { day } = useParams();
    const navigate = useNavigate();
    const user = useAuthStore((state) => state.user);
    const { week: loadedWeek, form: loadedForm, divisions } = useLoaderData() as LoaderData;
    const [weekForm, setWeekForm] = useState<LocatorWeekForm>(loadedForm);
    const [isBusy, setIsBusy] = useState(false);

    useEffect(() => {
        setWeekForm(loadedForm);
    }, [loadedForm]);

    const weeks = useMemo(() => buildWeekNumArray(
        user?.hireDate && user.hireDate > locatorStartDate ? user.hireDate : locatorStartDate,
        user?.quitDate && new Date() > new Date(user.quitDate.replace(/-/g, "/")) ? user.quitDate : (new MyDate()).s(),
    ).reverse(), [user?.hireDate, user?.quitDate]);

    const activeWeek = weeks.includes(loadedWeek) ? loadedWeek : weeknum((new MyDate()).s());
    const emergency = weekForm.emergency ?? [];
    const normalDayIndex = useMemo(() => ["0", "1", "2", "3", "4", "5", "6"].includes(day || "") ? Number(day) : false, [day]);
    const emergencyDayIndex = useMemo(() => {
        if (!day || !day.startsWith("e")) return false;
        const index = Number.parseInt(day.slice(1), 10);
        return Number.isInteger(index) && index >= 0 && index < emergency.length ? index : false;
    }, [day, emergency.length]);

    const renderedForm = useMemo(
        () => weekForm ?? JSON.parse(defaultEmptyLocatorDataString),
        [weekForm]
    );

    const colorCode = (status: string) => <span style = {{ color: statusColorCode(status) }}>{status}</span>;

    const openPdf = async (id: string, fileName: string) => {
        const formData = new FormData();
        formData.append("id", id);
        setIsBusy(true);
        const res = await myAxios({ fileName: "outside/locator/getPDF.php", formData });
        setIsBusy(false);
        if (!res?.data) return;
        const url = typeof res.data === "string" ? res.data : "";
        if (!url) return;
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        anchor.click();
    };

    const addEmergency = async () => {
        const formData = new FormData();
        formData.append("action", "AddEmergency");
        formData.append("wk", activeWeek);
        setIsBusy(true);
        const res = await myAxios({ fileName: "outside/locator/locator.php", formData });
        setIsBusy(false);
        if (!res) return;
        setWeekForm((current) => ({
            ...current,
            emergency: [...(current.emergency ?? []), { data: [], status: "Created", overnight: false }],
        }));
    };

    const deleteEmergency = async (index: number) => {
        if (!window.confirm("Are you sure to delete this emergency call out?")) return;
        const formData = new FormData();
        formData.append("action", "DeleteEmergency");
        formData.append("wk", activeWeek);
        formData.append("index", String(index));
        setIsBusy(true);
        const res = await myAxios({ fileName: "outside/locator/locator.php", formData });
        setIsBusy(false);
        if (!res) return;
        setWeekForm((current) => ({
            ...current,
            emergency: (current.emergency ?? []).filter((_, itemIndex) => itemIndex !== index),
        }));
    };

    const submitWeek = async () => {
        const invalidDays: Array<LocatorDay & { date?: string }> = renderedForm.form
        .map((item, index) => ({ ...item, date: getDatefromWeekNum(activeWeek, index) }))
        .filter((item) => item.status !== "Submitted");
        if (invalidDays.length) {
            window.alert(`You need to submit following date(s) of time card first:\n${invalidDays.map((item) => item.date).join(", ")}`);
            return;
        }
        const invalidEmergency: Array<LocatorDay & { label: string }> = (renderedForm.emergency ?? [])
        .map((item, index) => ({ ...item, label: `Emergency ${index + 1}` }))
        .filter((item) => item.status !== "Submitted");
        if (invalidEmergency.length) {
            window.alert(`You need to submit following date(s) of emergency time card first:\n${invalidEmergency.map((item) => item.label).join(", ")}`);
            return;
        }
        if (!window.confirm("Are you sure to submit for the week?\n(Note: after this submission, you are not able to edit this week.)")) return;
        const formData = new FormData();
        formData.append("action", "SubmitWeek");
        formData.append("wk", activeWeek);
        setIsBusy(true);
        const res = await myAxios({ fileName: "outside/locator/locator.php", formData });
        setIsBusy(false);
        if (!res) return;
        setWeekForm((current) => ({ ...current, status: "Submitted" }));
    };

    return (
        <div className = "cssTab">
            <nav className = "d-flex flex-wrap align-items-center justify-content-between gap-3 px-3 py-2 border-bottom">
                <div className = "fw-bold">Locator</div>
                <div className = "d-flex flex-wrap align-items-center gap-2">
                    <label htmlFor = "locatorWeek" className = "mb-0">Week</label>
                    <select
                        id = "locatorWeek"
                        className = "form-control w-auto"
                        value = {activeWeek}
                        onChange = {(e) => navigate(`/LMTimeCard/${e.currentTarget.value}`)}
                    >
                        {weeks.map((value) => <option key = {value} value = {value}>{getDateEndFromWeekNum(value)}</option>)}
                    </select>
                </div>
            </nav>
            <div className = "cssContent pb8rem">
                <div className = "h3 text-danger fw-bold mt-2 mb-0 ms-2">{getDateEndFromWeekNum(activeWeek)}</div>
                {["Submitted", "Approved"].includes(renderedForm.status || "") ? (
                    <div className = "h5 text-success mb-0 mt-2 ms-2">Submitted on {renderedForm.submitTime}</div>
                ) : null}
                {isBusy ? <div className = "cssLoading">Loading...</div> : null}
                <div className = "cssSeperateWhenWide px-1 mt-3 overflow-auto">
                    {weekDays.map((weekday, index) => {
                        const item = renderedForm.form[index] || { status: "Created" };
                        return (
                            <div className = "px-1" key = {index}>
                                <div className = "h4 text-secondary">{weekday} ({getDatefromWeekNum(activeWeek, index)})</div>
                                <div className = "my-3">
                                    <span className = "fw-bold">Status: {colorCode(item.status || "Created")}</span>
                                </div>
                                <div className = "mt-2">
                                    {!["Submitted", "Approved"].includes(renderedForm.status || "") ? (
                                        <button
                                            type = "button"
                                            className = "btn btn-success"
                                            onClick = {() => navigate(`/LMTimeCard/${activeWeek}/${index}`)}
                                        >
                                            Edit
                                        </button>
                                    ) : null}
                                    {item.status === "Submitted" ? (
                                        ((item.workStatus === "Work" || item.workStatus === undefined) && item.id && String(item.id).length === 32) ? (
                                            <button
                                                type = "button"
                                                className = "btn btn-info ms-2"
                                                onClick = {() => openPdf(
                                                    String(item.id),
                                                    `${(getDatefromWeekNum(activeWeek, index) || `day-${index + 1}`).replace(/\//g, "-")}.pdf`
                                                )}
                                            >
                                                PDF
                                            </button>
                                        ) : (
                                            <span className = "fw-bold ms-2">{item.workStatus}</span>
                                        )
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className = "d-flex px-1 mt-3">
                    {emergency.map((item, index) => (
                        <div className = "px-1" key = {index}>
                            <div className = "h4 text-secondary">Emergency {index + 1}</div>
                            <div className = "text-secondary">{item.date}</div>
                            <div className = "mt-2">
                                <span className = "fw-bold">Status: {colorCode(item.status || "Created")}</span>
                            </div>
                            <div className = "mt-2">
                                {!["Submitted", "Approved"].includes(renderedForm.status || "") ? (
                                    <>
                                        <button
                                            type = "button"
                                            className = "btn btn-success"
                                            onClick = {() => navigate(`/LMTimeCard/${activeWeek}/e${index}`)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            type = "button"
                                            className = "btn btn-danger ms-2"
                                            onClick = {() => deleteEmergency(index)}
                                        >
                                            Delete
                                        </button>
                                    </>
                                ) : null}
                                {item.status === "Submitted" ? (
                                    ((item.workStatus === "Work" || item.workStatus === undefined) && item.id && String(item.id).length === 32) ? (
                                        <button
                                            type = "button"
                                            className = "btn btn-info ms-2"
                                            onClick = {() => openPdf(String(item.id), `${item.date || `emergency-${index + 1}`}.pdf`)}
                                        >
                                            PDF
                                        </button>
                                    ) : (
                                        <span className = "fw-bold ms-2">{item.workStatus}</span>
                                    )
                                ) : null}
                            </div>
                        </div>
                    ))}
                </div>

                {!["Submitted", "Approved"].includes(renderedForm.status || "") ? (
                    <div className = "text-right mt-2 pb-3">
                        <button type = "button" className = "btn btn-warning mx-3" onClick = {addEmergency}>
                            Add Emergency Call Out
                        </button>
                        <button type = "button" className = "btn btn-primary mx-3" onClick = {submitWeek}>
                            Final submission for the week
                        </button>
                    </div>
                ) : null}
            </div>

            {normalDayIndex !== false ? (
                <LocatorForm
                    closeIt = {() => navigate(`/LMTimeCard/${activeWeek}`)}
                    defaultData = {renderedForm}
                    week = {activeWeek}
                    dayIndex = {normalDayIndex}
                    admin = {false}
                    userId = {undefined}
                    email = {undefined}
                    userName = {undefined}
                    lanId = {undefined}
                    outside = {user?.outside}
                    divisions = {divisions}
                    onWeekFormChange = {setWeekForm}
                />
            ) : null}
            {emergencyDayIndex !== false ? (
                <LocatorEmergencyForm
                    closeIt = {() => navigate(`/LMTimeCard/${activeWeek}`)}
                    defaultData = {renderedForm}
                    week = {activeWeek}
                    dayIndex = {emergencyDayIndex}
                    admin = {false}
                    userId = {undefined}
                    email = {undefined}
                    userName = {undefined}
                    lanId = {undefined}
                    divisions = {divisions}
                    onWeekFormChange = {setWeekForm}
                />
            ) : null}
        </div>
    );
}
